import { AccountModel, Button, Checkbox, CountryDropdown, Divider, GenericWrapperContext, googlePlacesMapper, Icon, InformationGroup, initFieldValidation, LocationTextField, PhoneTextField, removeEmptyProperties, TAccountAddress, TEventTarget, TextField, TFieldValidation, Typography, validateGenericInput, validateUrl } from "@cakemail-org/ui-components-v2"
import { ChangeEvent, ReactNode, useContext, useEffect, useMemo, useState } from "react"
import { Box, SelectChangeEvent, Stack } from "@mui/material"
import { useTranslation } from "react-i18next"
import "./styles.scss"
import { TOnboardingCompanySetup } from "./types"
import { OnboardingContext } from "../../context"
import { geocodeByAddress } from "react-places-autocomplete"
import {
    defaultCountries, getCountry
} from 'react-international-phone'
export function OnboardingCompanySetup({ }: TOnboardingCompanySetup) {
    const onboardingContext = useContext(OnboardingContext)
    const tmpAccount = onboardingContext.companyForm
    const { t } = useTranslation()
    const [location, setLocation] = useState<string>("")
    const [fieldStates, setFieldStates] = useState<Record<string, TFieldValidation>>({
        ...initFieldValidation("website", { value: tmpAccount.website })
    })
    const [continueDisabled, setContinueDisabled] = useState<boolean>(false)
    const [infoReviewed, setInfoReviewed] = useState<boolean>(true)
    const provinceIsRequired = tmpAccount.address?.country === "canada" || tmpAccount.address?.country?.includes("united states")

    useEffect(() => {
        if (onboardingContext?.enrichedPerson?.organization) {
            const org = onboardingContext.enrichedPerson.organization
            const tmpAccount = new AccountModel({
                name: org.name,
                website: org.website_url,
                address: {
                    address1: org.street_address,
                    address2: "",
                    city: org.city,
                    province: org.state,
                    country: org.country,
                    postal_code: org.postal_code
                },
                phone: org.primary_phone?.sanitized_number,
            })
            onboardingContext.setCompanyForm(tmpAccount)
            setInfoReviewed(false)
        }
    }, [])

    useEffect(() => {
        let hasError = false

        for (const [key, value] of Object.entries(fieldStates)) {
            hasError = value.error || false
            if (hasError) {
                break
            }
        }

        const partialAddress: Partial<TAccountAddress> = removeEmptyProperties(onboardingContext.companyForm.address) || {}
        let hasNotMinAddressFields = false

        if (
            ((partialAddress?.address1 || partialAddress?.country) && (!partialAddress?.address1 || !partialAddress?.country)) ||
            (!partialAddress?.province && provinceIsRequired)
        )
            hasNotMinAddressFields = true

        if (!onboardingContext.companyForm.name || hasError || !infoReviewed || hasNotMinAddressFields) {
            setContinueDisabled(true)
        } else {
            setContinueDisabled(false)
        }
    }, [onboardingContext.companyForm, infoReviewed])

    function handleTxtChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        handleChange(e.target)
    }
    function handleDDChange(e: SelectChangeEvent<string>, child: ReactNode, previousValue?: string): any {
        handleChange(e.target)
    }
    function handlePhoneField(val: string) {
        handleChange({ name: "phone", value: val })
    }
    function handleTxtFieldTimeout(e: ChangeEvent<HTMLInputElement>) {
        handleChange(e.target)
        validateField(e.target)
    }

    function validateField({ name, value }: { name: string, value: string }) {
        if (fieldStates[name]) {
            if (name === "website") {
                setFieldStates({
                    ...fieldStates,
                    "website": validateUrl(value)
                })
            }
        }
    }

    function handleChange({ name, value }: { name: string, value: string }) {
        const nestedName = name.split(".")
        if (nestedName.length > 1) {
            tmpAccount.set(nestedName[0] as keyof AccountModel, {
                ...tmpAccount[nestedName[0] as keyof AccountModel],
                [nestedName[1]]: value
            })
        } else {
            tmpAccount.set(name as keyof AccountModel, value)
        }
        onboardingContext.setCompanyForm(new AccountModel(tmpAccount))
    }

    function handleLocationField(val: string) {
        setLocation(val)
    }

    function handleLocationSelect(val: string) {
        if (tmpAccount) {
            geocodeByAddress(val)
                .then((results: google.maps.GeocoderResult[]) => {
                    const address = googlePlacesMapper(results[0])
                    tmpAccount.set("address", {
                        address2: tmpAccount.address?.address2,
                        address1: address.address,
                        country: address.countryCode || address.country,
                        province: address.state,
                        city: address.city,
                        postal_code: address.postal
                    })
                    setLocation("")
                })
                .catch((error: google.maps.GeocoderResponse) => {
                    setLocation("")
                })
        }
    }

    function handleNext() {
        onboardingContext.handleNext()
    }

    function handleCheckboxChangeTxt() {
        setInfoReviewed(!infoReviewed)
    }

    function handleCheckboxChange(e: ChangeEvent<HTMLInputElement>) {
        setInfoReviewed(e.target.checked)
    }

    const defaultIsoCountry = useMemo(() => {
        return defaultCountries.find(([name, iso, code]) => name === tmpAccount.address?.country)?.[1]
    }, [tmpAccount.address?.country])


    return <Stack direction="column" className="onboardingCompanySetup" gap={9} flex={1}>
        <TextField
            name="name"
            label={`${t("general-org-name")} (${t("general-required")})`}
            placeholder={t("general-org-name-placeholder")}
            onChange={handleTxtChange}
            value={tmpAccount.name || ""}
        />
        <TextField
            name="website"
            value={tmpAccount.website}
            label={t("general-website")}
            placeholder={t("general-url-placeholder")}
            onChange={handleTxtChange}
            error={fieldStates.website.error}
            helperText={fieldStates.website.error ? t(fieldStates.website.message || "") : ""}
            timeout={{
                callback: handleTxtFieldTimeout
            }}
        />

        <LocationTextField
            iconName="Search18"
            name="address"
            placeholder={t("address-location-placeholder")}
            onChange={handleLocationField}
            onSelect={handleLocationSelect}
            value={location}
        />

        <PhoneTextField
            key={tmpAccount.address?.country}
            name="phone"
            label={t("general-phone")}
            placeholder={t("general-phone-placeholder")}
            onChange={handlePhoneField}
            value={tmpAccount.phone || ""}
            defaultCountry={defaultIsoCountry}
        />

        <TextField
            name="address.address1"
            label={t("general-address-line1")}
            placeholder={t("general-address-line1-placeholder")}
            onChange={handleTxtChange}
            value={tmpAccount.address?.address1 || ""}
        />
        <TextField
            name="address.address2"
            label={t("general-address-line2")}
            placeholder={t("general-address-line2-placeholder")}
            onChange={handleTxtChange}
            value={tmpAccount.address?.address2 || ""}
        />
        <TextField
            name="address.city"
            label={t("general-city")}
            placeholder={t("general-city-placeholder")}
            onChange={handleTxtChange}
            value={tmpAccount.address?.city || ""}
        />
        <TextField
            required={provinceIsRequired}
            name="address.province"
            label={`${t("general-province")} ${provinceIsRequired ? t("form-required") : ""}`}
            placeholder={t("general-province-placeholder")}
            onChange={handleTxtChange}
            value={tmpAccount.address?.province || ""}
        />

        <CountryDropdown
            size="large"
            defaultValue={""}
            label={t("general-country")}
            placeholder={t("general-country-placeholder")}
            name="address.country"
            variant="outlined"
            onChange={handleDDChange}
            value={tmpAccount.address?.country || ""}
        />

        <TextField
            className="postalCodeField"
            name="address.postal_code"
            label={t("general-postal")}
            placeholder={t("general-postal-placeholder")}
            onChange={handleTxtChange}
            value={tmpAccount.address?.postal_code || ""}
        />

        <Stack direction="row" className="onboardingContent" justifyContent="space-between" gap={8}>
            <Stack direction="row" gap={2} alignItems="center" className="onboardingInfoReviewed">
                {onboardingContext.enrichedPerson?.organization && <>
                    <Checkbox
                        color="secondary"
                        onChange={handleCheckboxChange}
                        checked={infoReviewed}
                    />
                    <Typography onClick={handleCheckboxChangeTxt} variant="body2S">{t("onboarding-company-info-reviewed")}</Typography>
                </>
                }
            </Stack>
            <Button variant="contained" color="secondary" onClick={handleNext} disabled={continueDisabled}>
                {t("general-continue")}
            </Button>
        </Stack>

    </Stack>
}

export default OnboardingCompanySetup